import React, { useState } from "react";
import { FiLoader } from "react-icons/fi";
import "./QaAutoPopulation.css";
import { useAuth0 } from "@auth0/auth0-react";

import { approveAnswerApi, manualAnswerApi, qaResetApi } from "../../services/AxiosServices";

type QaAutoPopulationProps = {
    fetchProjectInfo?: (selectedRowData: any, projectName: string) => void;
    header: string;
    status: string;
    options: { id: string; answer: string; used_sources: string }[];
}

const QaAutoPopulation: React.FC<QaAutoPopulationProps> = ({ fetchProjectInfo, header, status, options }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [manualText, setManualText] = useState("");
    const [loadingApprove, setLoadingApprove] = useState<string>("");
    const [loadingConfirm, setLoadingConfirm] = useState(false);

    const handleApproveClick = async (qa_id) => {
        setLoadingApprove(qa_id);
        const token = await getAccessTokenSilently();
        const formData = new FormData();
        formData.append("qa_id", qa_id);
        try {
            if (status.toLowerCase() !== "approved") {
                await approveAnswerApi(formData, token);
                await fetchProjectInfo?.(null, "");
            } else {
                await qaResetApi(formData, token);
                await fetchProjectInfo?.(null, "");
            }
        } catch (error) {
            console.error("Error approving/resetting answer:", error);
        } finally {
            setLoadingApprove("");
        }
    };

    const handleConfirmClick = async () => {
        setLoadingConfirm(true);
        const token = await getAccessTokenSilently();
        const formData = new FormData();
        formData.append("new_answer", manualText);
        formData.append("qa_id", options[0]?.id || "");
        try {
            let response = await manualAnswerApi(formData, token);
            if (response && response['success']) {
                await fetchProjectInfo?.(null, "");
                setManualText("");
            }
        } catch (error) {
            console.error("Error submitting manual response:", error);
        } finally {
            setLoadingConfirm(false);
        }
    };

    return (
        <div className="qa-container">
            <h4>{status === "approved" ? "Approved" : "Review"}</h4>
            <div className="qa-header">
                <h1>{header}</h1>
            </div>
            <div className="qa-options">
                {options.map((option, index) => (
                    <React.Fragment key={index}>
                        {status.toLowerCase() !== "approved" &&
                            <h3>{`Option ${index + 1}`}</h3>
                        }

                        <div className="qa-option-container">
                            <div className={`qa-option${status.toLowerCase() === 'approved' ? ' approved' : ''}`}>
                                <p>Answer</p>
                                <textarea
                                    name={`option_${index}`}
                                    value={option.answer}
                                    readOnly
                                />
                            </div>

                            <div className={`qa-option${status.toLowerCase() === 'approved' ? ' approved' : ''}`}>
                                <p>Source Reference</p>
                                <textarea
                                    name={`Source_${index}`}
                                    value={option.used_sources}
                                    readOnly
                                />
                            </div>
                        </div>
                        <button className="qa-approve button"
                            onClick={() => handleApproveClick(option.id)}
                            disabled={!option.answer.trim() || loadingApprove === option.id}
                            style={!option.answer.trim() || loadingApprove === option.id ? { background: "#7a7a7a", cursor: "not-allowed" } : {}}>
                            {loadingApprove === option.id ? <FiLoader className="loading-icon" /> : (status.toLowerCase() !== "approved" ? "Approve" : "Reset")}
                        </button>
                    </React.Fragment>
                ))}
            </div>
            {status.toLowerCase() !== "approved" &&
                <div className="qa-manual-response">
                    <h2>Manual Response</h2>
                    <div className="qa-manual-response-input">
                        <input
                            type="text"
                            value={manualText}
                            onChange={(e) => setManualText(e.target.value)}
                            placeholder="Write response to be added as an answer"
                        />
                        <button
                            disabled={!manualText.trim() || loadingConfirm}
                            onClick={handleConfirmClick}
                            className="qa-confirm button"
                            style={!manualText.trim() || loadingConfirm ? { background: "#7a7a7a", cursor: "not-allowed" } : {}}
                        >
                            {loadingConfirm ? <FiLoader className="loading-icon" /> : "Confirm"}
                        </button>
                    </div>
                </div>
            }

        </div>
    );
};

export default QaAutoPopulation;